import axios from 'axios';
import { API_ENDPOINTS } from '../config';

class UploadService {
  constructor() {
    this.activeUploads = new Map();
    this.chunkSize = 5 * 1024 * 1024; // 5MB chunks for better performance
  }

  async startUpload(taskName, fileType, file, onProgress) {
    const uploadId = await this._initiateUpload(taskName, fileType, file.size);
    
    const upload = {
      id: uploadId,
      file,
      fileType,
      taskName,
      totalChunks: Math.ceil(file.size / this.chunkSize),
      uploadedChunks: new Set(),
      failedChunks: new Set(),
      onProgress,
      status: 'uploading'
    };

    this.activeUploads.set(uploadId, upload);
    
    // Start uploading chunks
    await this._uploadChunks(upload);
    
    return uploadId;
  }

  async _initiateUpload(taskName, fileType, totalSize) {
    const formData = new FormData();
    formData.append('task_name', taskName);
    formData.append('file_type', fileType);
    formData.append('total_size', totalSize);

    const token = localStorage.getItem('token');
    const response = await axios.post(API_ENDPOINTS.UPLOAD_START, formData, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data.upload_id;
  }

  async _uploadChunks(upload) {
    const token = localStorage.getItem('token');
    
    for (let chunkNumber = 0; chunkNumber < upload.totalChunks; chunkNumber++) {
      if (upload.status === 'cancelled') {
        break;
      }

      try {
        await this._uploadChunk(upload, chunkNumber, token);
        upload.uploadedChunks.add(chunkNumber);
        
        // Update progress
        const progress = (upload.uploadedChunks.size / upload.totalChunks) * 100;
        if (upload.onProgress) {
          upload.onProgress(progress);
        }
        
      } catch (error) {
        console.error(`Failed to upload chunk ${chunkNumber}:`, error);
        upload.failedChunks.add(chunkNumber);
        
        // Retry failed chunk up to 100 times
        let retryCount = 0;
        const maxRetries = 100;
        
        while (retryCount < maxRetries) {
          try {
            retryCount++;
            console.log(`Retrying chunk ${chunkNumber}, attempt ${retryCount}/${maxRetries}`);
            await this._uploadChunk(upload, chunkNumber, token);
            upload.uploadedChunks.add(chunkNumber);
            upload.failedChunks.delete(chunkNumber);
            break; // Success, exit retry loop
          } catch (retryError) {
            console.error(`Retry attempt ${retryCount} failed for chunk ${chunkNumber}:`, retryError);
            if (retryCount >= maxRetries) {
              console.error(`All ${maxRetries} retry attempts failed for chunk ${chunkNumber}`);
              upload.status = 'failed';
              throw new Error(`Failed to upload chunk ${chunkNumber} after ${maxRetries} retry attempts`);
            }
            // Continue to next retry attempt
          }
        }
      }
    }

    if (upload.status !== 'cancelled' && upload.status !== 'failed') {
      upload.status = 'completed';
    }
  }

  async _uploadChunk(upload, chunkNumber, token) {
    const start = chunkNumber * this.chunkSize;
    const end = Math.min(start + this.chunkSize, upload.file.size);
    const chunk = upload.file.slice(start, end);

    const formData = new FormData();
    formData.append('upload_id', upload.id);
    formData.append('chunk_number', chunkNumber);
    formData.append('chunk_data', chunk);

    await axios.post(API_ENDPOINTS.UPLOAD_CHUNK, formData, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      timeout: 300000 // 5 minutes timeout per chunk for large files
    });
  }

  async finalizeUpload(uploadId) {
    const upload = this.activeUploads.get(uploadId);
    if (!upload) {
      throw new Error('Upload not found');
    }

    if (upload.status !== 'completed') {
      throw new Error('Upload is not completed');
    }

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('upload_id', uploadId);

    const response = await axios.post(API_ENDPOINTS.UPLOAD_FINALIZE, formData, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    // Cleanup
    this.activeUploads.delete(uploadId);
    
    return response.data.file_path;
  }

  async getUploadStatus(uploadId) {
    const upload = this.activeUploads.get(uploadId);
    if (!upload) {
      return null;
    }

    return {
      status: upload.status,
      progress: (upload.uploadedChunks.size / upload.totalChunks) * 100,
      uploadedChunks: upload.uploadedChunks.size,
      totalChunks: upload.totalChunks,
      failedChunks: upload.failedChunks.size
    };
  }

  cancelUpload(uploadId) {
    const upload = this.activeUploads.get(uploadId);
    if (upload) {
      upload.status = 'cancelled';
      this.activeUploads.delete(uploadId);
    }
  }

  pauseUpload(uploadId) {
    const upload = this.activeUploads.get(uploadId);
    if (upload && upload.status === 'uploading') {
      upload.status = 'paused';
    }
  }

  resumeUpload(uploadId) {
    const upload = this.activeUploads.get(uploadId);
    if (upload && upload.status === 'paused') {
      upload.status = 'uploading';
      this._uploadChunks(upload);
    }
  }

  // Resume interrupted uploads
  async resumeInterruptedUpload(uploadId) {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_ENDPOINTS.UPLOAD_STATUS}/${uploadId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const status = response.data;
      if (status) {
        // Recreate upload object from status
        const upload = {
          id: uploadId,
          status: 'uploading',
          uploadedChunks: new Set(),
          totalChunks: Math.ceil(status.total_size / this.chunkSize),
          onProgress: null
        };

        this.activeUploads.set(uploadId, upload);
        
        // Continue uploading remaining chunks
        await this._uploadChunks(upload);
        return uploadId;
      }
    } catch (error) {
      console.error('Failed to resume upload:', error);
      throw error;
    }
  }
}

const uploadService = new UploadService();
export default uploadService; 